/**
 * Código viejo tras un despliegue. Cloudflare Pages solo sirve los ficheros de la
 * versión nueva: quien tenía la web abierta de antes y navega a una ruta lazy pide un
 * chunk que ya no existe y el import dinámico falla. La salida es recargar para bajar
 * el `index.html` nuevo — una sola vez: si vuelve a fallar enseguida, el problema es
 * otro y recargar en bucle solo empeoraría las cosas.
 *
 * Mismo remedio para un chunk que no termina de llegar (`chunkLoad`, atasco al volver de
 * segundo plano en la app de iPhone): recargar es lo que el usuario hacía a mano.
 */

const STORAGE_KEY = 'mp:stale-chunk-reload-at'
/** Ventana en la que un segundo fallo NO vuelve a recargar (evita bucles). */
export const STALE_CHUNK_RELOAD_GUARD_MS = 10_000

const STALE_CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome / Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite (CSS de un chunk)
  /Timed out loading module chunk/i, // `chunkLoad`: el chunk no llegó a tiempo
]

export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message: unknown }).message)
        : String(error ?? '')
  return STALE_CHUNK_PATTERNS.some((re) => re.test(message))
}

interface ReloadDeps {
  reload?: () => void
  now?: () => number
  storage?: Pick<Storage, 'getItem' | 'setItem'>
}

/**
 * Recarga la página si no se ha recargado por este motivo en los últimos
 * {@link STALE_CHUNK_RELOAD_GUARD_MS}. Devuelve `true` si ha recargado.
 * Sin `sessionStorage` (modo privado estricto, sandbox) no recarga: preferimos
 * enseñar la pantalla de error a arriesgar un bucle.
 */
export function reloadOnceForStaleChunk({
  reload = () => window.location.reload(),
  now = () => Date.now(),
  storage,
}: ReloadDeps = {}): boolean {
  let store: Pick<Storage, 'getItem' | 'setItem'>
  try {
    store = storage ?? window.sessionStorage
    const last = Number(store.getItem(STORAGE_KEY) ?? 0)
    if (now() - last < STALE_CHUNK_RELOAD_GUARD_MS) return false
    store.setItem(STORAGE_KEY, String(now()))
  } catch {
    return false
  }
  reload()
  return true
}
